/**
 * Model Registry API Service
 *
 * Handles API tokens, model repositories, and registry operations.
 */
import API from './api.ts';

// =============================================================================
// API Tokens
// =============================================================================

/**
 * Create a new API token.
 * @param {string} name - Descriptive name for the token
 * @param {string|null} expiresAt - Optional expiration date (ISO format)
 * @returns {Promise} Response with token data (including plaintext token shown only once)
 */
export const createApiToken = async (name, expiresAt = null) => {
  const data = { name };
  if (expiresAt) {
    data.expires_at = expiresAt;
  }
  return API.post('/registry/tokens/', data);
};

/**
 * List all API tokens for the current user.
 * @returns {Promise} Response with array of token objects
 */
export const listApiTokens = async () => {
  return API.get('/registry/tokens/');
};

/**
 * Get details of a specific token.
 * @param {number} tokenId - Token ID
 * @returns {Promise} Response with token details
 */
export const getApiToken = async (tokenId) => {
  return API.get(`/registry/tokens/${tokenId}/`);
};

/**
 * Revoke (delete) an API token.
 * @param {number} tokenId - Token ID to revoke
 * @returns {Promise} Response (204 No Content on success)
 */
export const revokeApiToken = async (tokenId) => {
  return API.delete(`/registry/tokens/${tokenId}/`);
};

// =============================================================================
// Model Repositories
// =============================================================================

/**
 * List model repositories.
 * @param {Object} options - Filter options
 * @param {boolean} options.mine - Only show user's repositories
 * @param {boolean} options.public - Only show public repositories
 * @param {string} options.type - Filter by model type (segmentation, generation, etc.)
 * @param {string} options.repo_type - Filter by repository type (model, dataset, study)
 * @returns {Promise} Response with array of repository objects
 */
export const listRepositories = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.mine) params.append('mine', 'true');
  if (options.public) params.append('public', 'true');
  if (options.type) params.append('type', options.type);
  if (options.repo_type) params.append('repo_type', options.repo_type);

  return API.get(`/registry/repos/?${params.toString()}`);
};

/**
 * Create a new model repository.
 * @param {Object} data - Repository data
 * @param {string} data.name - Repository name
 * @param {string} data.description - Description
 * @param {string} data.model_type - Model type (segmentation, generation, etc.)
 * @param {boolean} data.is_public - Whether repository is public
 * @returns {Promise} Response with created repository
 */
export const createRepository = async (data) => {
  return API.post('/registry/repos/', data);
};

/**
 * Get repository details.
 * @param {string} owner - Repository owner username
 * @param {string} name - Repository name
 * @returns {Promise} Response with repository details
 */
export const getRepository = async (owner, name) => {
  return API.get(`/registry/repos/${owner}/${name}/`);
};

/**
 * Update repository metadata.
 * @param {string} owner - Repository owner username
 * @param {string} name - Repository name
 * @param {Object} data - Fields to update (description, is_public)
 * @returns {Promise} Response with updated repository
 */
export const updateRepository = async (owner, name, data) => {
  return API.patch(`/registry/repos/${owner}/${name}/`, data);
};

/**
 * Delete a repository.
 * @param {string} owner - Repository owner username
 * @param {string} name - Repository name
 * @returns {Promise} Response (204 No Content on success)
 */
export const deleteRepository = async (owner, name) => {
  return API.delete(`/registry/repos/${owner}/${name}/`);
};

// =============================================================================
// Tags
// =============================================================================

/**
 * List tags for a repository.
 * @param {string} owner - Repository owner username
 * @param {string} name - Repository name
 * @returns {Promise} Response with array of tag objects
 */
export const listTags = async (owner, name) => {
  return API.get(`/registry/repos/${owner}/${name}/tags/`);
};

/**
 * Create a new tag.
 * @param {string} owner - Repository owner username
 * @param {string} name - Repository name
 * @param {Object} data - Tag data
 * @param {string} data.name - Tag name (e.g., 'v1.0.0')
 * @param {string} data.commit_sha - Commit SHA (optional, defaults to HEAD)
 * @param {string} data.message - Release notes (optional)
 * @returns {Promise} Response with created tag
 */
export const createTag = async (owner, name, data) => {
  return API.post(`/registry/repos/${owner}/${name}/tags/`, data);
};

/**
 * Delete a tag.
 * @param {string} owner - Repository owner username
 * @param {string} name - Repository name
 * @param {string} tagName - Tag name to delete
 * @returns {Promise} Response (204 No Content on success)
 */
export const deleteTag = async (owner, name, tagName) => {
  return API.delete(`/registry/repos/${owner}/${name}/tags/${tagName}/`);
};

// =============================================================================
// User Info
// =============================================================================

/**
 * Get current user info (whoami).
 * @returns {Promise} Response with user info
 */
export const whoami = async () => {
  return API.get('/registry/whoami/');
};

// =============================================================================
// Build from Registry
// =============================================================================

/**
 * Trigger a build from a registry repository.
 * This clones the repo and builds the Docker image.
 * @param {number} repoId - Repository ID
 * @returns {Promise} Response with build task info
 */
export const buildFromRegistry = async (repoId) => {
  return API.post(`/registry/repos/build/`, { repo_id: repoId });
};

/**
 * Get commit history for a repository.
 * @param {string} owner - Repository owner username
 * @param {string} name - Repository name
 * @param {number} limit - Maximum number of commits to return
 * @returns {Promise} Response with array of commit objects
 */
export const getCommitHistory = async (owner, name, limit = 20) => {
  return API.get(`/registry/repos/${owner}/${name}/commits/?limit=${limit}`);
};

/**
 * Trigger import/rebuild of a repository model.
 * @param {string} owner - Repository owner username
 * @param {string} name - Repository name
 * @returns {Promise} Response with import status
 */
export const triggerImport = async (owner, name) => {
  return API.post(`/registry/repos/${owner}/${name}/import/`);
};
